import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import DeleteIcon from "@mui/icons-material/Delete";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import { Box, CircularProgress, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import { Styled } from "./styles";
import { useUpload } from "./hooks";
import { UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Forms } from "@/types/form";
import { i18n } from "@/utils/i18n";

interface Props {
    setValue: UseFormSetValue<Forms["Profile"]>;
    watch: UseFormWatch<Forms["Profile"]>;
    firstLetter: string;
    bgColor: string;
}

const FileUpload: React.FC<Props> = ({ setValue, watch, firstLetter, bgColor }) => {
    const { onUpload, onRemove, loading } = useUpload(setValue, watch);
    const file = watch("photoURL");

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const onOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const onCloseMenu = () => setAnchorEl(null);

    const onResetInput = () => {
        if (inputRef.current && inputRef.current.value) {
            inputRef.current.value = "";
        }
    };

    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    if (loading) {
        return (
            <Styled.Container>
                <CircularProgress />
            </Styled.Container>
        );
    }

    return (
        <Styled.Container>
            <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {file ? (
                    <Styled.Placeholder
                        data-testid="image-upload-placeholder"
                        type="button"
                        disableRipple={true}
                        variant="text"
                        onClick={onOpenMenu}
                    >
                        <Styled.Image src={file} />
                        <Styled.IconOverlay style={{ opacity: isHovered ? 1 : 0 }}>
                            <AddAPhotoIcon style={{ opacity: isHovered ? 1 : 0 }} />
                        </Styled.IconOverlay>
                    </Styled.Placeholder>
                ) : (
                    <Styled.Placeholder
                        component="label"
                        type="button"
                        disableRipple={true}
                        variant="text"
                        sx={{ backgroundColor: bgColor }}
                    >
                        <Styled.FileInput
                            data-testid="brand-image-upload-input"
                            ref={inputRef}
                            type="file"
                            accept="image/png, image/jpeg, image/webp, image/avif"
                            onChange={onUpload}
                            onClick={onResetInput}
                        />
                        <Typography variant="h1">{firstLetter}</Typography>
                        <Styled.IconOverlay style={{ opacity: isHovered ? 1 : 0 }}>
                            <AddAPhotoIcon style={{ opacity: isHovered ? 1 : 0 }} />
                        </Styled.IconOverlay>
                    </Styled.Placeholder>
                )}
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={onCloseMenu}
                transformOrigin={{ horizontal: "center", vertical: "top" }}
                anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
                slotProps={{
                    paper: {
                        sx: { mt: 0.3 },
                    },
                }}
            >
                <MenuItem>
                    <ListItemIcon>
                        <ChangeCircleIcon />
                    </ListItemIcon>
                    <Styled.ChangeButton component="label" type="button" variant="text">
                        <Typography fontWeight={400}>
                            {i18n.t("button.changePhoto")}
                        </Typography>
                        <Styled.FileInput
                            data-testid="image-change-input"
                            ref={inputRef}
                            type="file"
                            name="photo"
                            accept="image/png, image/jpeg, image/webp"
                            onChange={e => onUpload(e, setAnchorEl)}
                            onClick={onResetInput}
                        />
                    </Styled.ChangeButton>
                </MenuItem>
                <MenuItem
                    data-testid="image-remove-input"
                    onClick={() => {
                        onRemove(), setAnchorEl(null);
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon />
                    </ListItemIcon>
                    <Typography fontWeight={400}>
                        {i18n.t("button.removePhoto")}
                    </Typography>
                </MenuItem>
            </Menu>
        </Styled.Container>
    );
};

export default FileUpload;
